import { AuthenticationResult, EventMessage, EventType as MsalEventType, SilentRequest } from '@azure/msal-browser';

import { ssoSilent } from '../authConfig';
import msalService from '../services/msalService';
import { getEnv } from '../utils/getConfig';
import { APIMSUBSCRIPTION_DEFAULT_VALUE } from '../utils/constants';
import { ATTR, Attributes, EventType } from './contract';

export const LOG_EVENT_PATH = 'stores/new-relic/log-event';

/** Events held while MSAL has no account yet; the oldest is dropped on overflow. */
export const PRE_ACCOUNT_BUFFER_MAX = 20;

/** A cached token is renewed when less than this is left (MSAL's own default renewal offset). */
export const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

/** Sends waiting for the in-flight token request; the oldest is dropped on overflow. */
export const PENDING_SEND_MAX = 20;

/** A token request still unsettled after this long is treated as failed by the next send. */
export const TOKEN_REQUEST_ABANDON_MS = 30_000;

/** At most one token warning (failed or abandoned request, pending overflow) per this interval. */
export const TOKEN_FAILURE_WARN_INTERVAL_MS = 60 * 1000;

/** MSAL 2.x events after which an account can be available. */
const ACCOUNT_EVENTS: ReadonlyArray<string> = [
  MsalEventType.LOGIN_SUCCESS,
  MsalEventType.ACQUIRE_TOKEN_SUCCESS,
  MsalEventType.HANDLE_REDIRECT_END,
];

const LOGOUT_EVENTS: ReadonlyArray<string> = [MsalEventType.LOGOUT_START, MsalEventType.LOGOUT_SUCCESS];

export interface EventPayload {
  event_type: EventType;
  store_num: string;
  timestamp: number;
  attributes: Attributes;
}

interface PendingSend {
  payload: EventPayload;
  /** Resolves the promise sendPayload returned for this payload. */
  done: () => void;
}

let baseUrl: string | null = null;

/** Per tab; consumed only when a request is actually issued, so a dropped event leaves no gap. */
let seq = 0;

let preAccountBuffer: EventPayload[] = [];

let overflowWarned = false;

let cachedToken: { accessToken: string; expiresOn: number } | null = null;

/** Set while the one token request is in flight; every send made meanwhile waits for it in pendingSends. */
let tokenRequestStartedAt: number | null = null;

/** Bumped on every token request, abandon and logout, so only the current request's result is used. */
let tokenGeneration = 0;

let pendingSends: PendingSend[] = [];

let lastTokenWarnAt: number | null = null;

let msalCallbackRegistered = false;

/** Set once a /log-event response carries telemetryEnabled: false; never cleared for the page's life. */
let disabledByServer = false;

let logoutListener: (() => void) | null = null;

/** Nothing is ever sent before this is called, which keeps unit tests and storybook-like renders silent. */
export const configureTransport = (url: string | null | undefined): void => {
  baseUrl = url ? (url.endsWith('/') ? url : `${url}/`) : null;
};

const readEnv = (key: string): string | undefined => {
  try {
    return getEnv(key);
  } catch (e) {
    return undefined;
  }
};

/** Only an explicit NR_EVENTS_ENABLED of "true" (trimmed, any case) enables sending; unset or empty disables. */
export const isTransportEnabled = (): boolean =>
  Boolean(baseUrl)
  && !disabledByServer
  && String(readEnv('NR_EVENTS_ENABLED') ?? '').trim().toLowerCase() === 'true';

const isWarnDue = (lastWarnAt: number | null, now: number): boolean =>
  lastWarnAt === null || now - lastWarnAt >= TOKEN_FAILURE_WARN_INTERVAL_MS;

const warnTokenFailure = (): void => {
  const now = Date.now();

  if (!isWarnDue(lastTokenWarnAt, now)) {
    return;
  }

  lastTokenWarnAt = now;
  console.warn('Telemetry: the access token could not be refreshed; events are dropped.');
};

const warnPendingOverflow = (): void => {
  const now = Date.now();

  if (!isWarnDue(lastTokenWarnAt, now)) {
    return;
  }

  lastTokenWarnAt = now;
  console.warn(`Telemetry: still waiting for an access token; only the newest ${PENDING_SEND_MAX} events are kept.`);
};

/** The cached token while it has more than TOKEN_REFRESH_MARGIN_MS left. */
const freshCachedToken = (): string | null =>
  (cachedToken && cachedToken.expiresOn - Date.now() > TOKEN_REFRESH_MARGIN_MS ? cachedToken.accessToken : null);

const acquireToken = async (): Promise<AuthenticationResult> => {
  const account = msalService.getAccount();

  if (!account) {
    throw new Error('No MSAL account');
  }

  const result = await msalService.getMsalInstance().acquireTokenSilent({
    ...ssoSilent,
    account,
  } as SilentRequest);

  if (!result?.accessToken) {
    throw new Error('No access token');
  }

  return result;
};

const post = (url: string, token: string, payload: EventPayload): Promise<unknown> => {
  try {
    seq += 1;

    return fetch(`${url}${LOG_EVENT_PATH}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': readEnv('APIMSUBSCRIPTION') || APIMSUBSCRIPTION_DEFAULT_VALUE,
      },
      body: JSON.stringify({ ...payload, attributes: { ...payload.attributes, [ATTR.seq]: seq }}),
    }).then(readServerBrake, () => undefined);
  } catch (e) {
    return Promise.resolve();
  }
};

const holdUntilAccount = (payload: EventPayload): void => {
  preAccountBuffer.push(payload);

  if (preAccountBuffer.length > PRE_ACCOUNT_BUFFER_MAX) {
    preAccountBuffer.shift();

    if (!overflowWarned) {
      overflowWarned = true;
      console.warn(`Telemetry: no signed-in account yet; only the newest ${PRE_ACCOUNT_BUFFER_MAX} events are kept.`);
    }
  }
};

const takePreAccountBuffer = (): EventPayload[] => {
  const batch = preAccountBuffer;

  preAccountBuffer = [];

  return batch;
};

const takePendingSends = (): PendingSend[] => {
  const sends = pendingSends;

  pendingSends = [];

  return sends;
};

const release = (sends: PendingSend[]): void => sends.forEach(({ done }) => done());

/** Drops the token request (a late result is ignored), the cache and every event waiting for a token. */
const dropTokenRequest = (): void => {
  tokenGeneration += 1;
  tokenRequestStartedAt = null;
  cachedToken = null;
  preAccountBuffer = [];
  release(takePendingSends());
};

const applyServerBrake = (body: unknown): void => {
  try {
    if (disabledByServer || typeof body !== 'object' || body === null
      || (body as { telemetryEnabled?: unknown }).telemetryEnabled !== false) {
      return;
    }

    disabledByServer = true;
    dropTokenRequest();
    console.warn('Telemetry: disabled by the server for the rest of this page.');
  } catch (e) {
    // Telemetry must never surface an error to the user.
  }
};

/**
 * The runtime kill switch: a 2xx whose JSON body has telemetryEnabled === false stops all sending. Anything else
 * (no or unparsable body, other status, other value) is no signal. The body is read without being awaited.
 */
const readServerBrake = (response: unknown): void => {
  try {
    const res = response as Partial<Response> | undefined;

    if (!res?.ok || typeof res.json !== 'function') {
      return;
    }

    res.json().then(applyServerBrake, () => undefined);
  } catch (e) {
    // No signal.
  }
};

const waitForToken = (payload: EventPayload): Promise<void> => {
  let done: () => void = () => undefined;
  const sent = new Promise<void>((resolve) => {
    done = resolve;
  });

  pendingSends.push({ payload, done });

  if (pendingSends.length > PENDING_SEND_MAX) {
    release(pendingSends.splice(0, 1));
    warnPendingOverflow();
  }

  return sent;
};

const onTokenSettled = (generation: number, result: AuthenticationResult | null): void => {
  if (generation !== tokenGeneration) {
    return;
  }

  if (!result) {
    dropTokenRequest();
    warnTokenFailure();

    return;
  }

  tokenRequestStartedAt = null;

  if (result.expiresOn) {
    cachedToken = { accessToken: result.accessToken, expiresOn: result.expiresOn.getTime() };
  }

  // The buffer is frozen while a request is in flight, so it holds only events older than the pending sends.
  const buffered = takePreAccountBuffer();
  const waiting = takePendingSends();
  // Re-read: the transport may have been reconfigured while the token was being acquired.
  const url = baseUrl;

  if (!url || !isTransportEnabled()) {
    release(waiting);

    return;
  }

  buffered.forEach((payload) => post(url, result.accessToken, payload));
  waiting.forEach(({ payload, done }) => post(url, result.accessToken, payload).then(done, done));
};

const startTokenRequest = (): void => {
  tokenGeneration += 1;

  const generation = tokenGeneration;

  cachedToken = null;
  tokenRequestStartedAt = Date.now();
  acquireToken()
    .then((result) => onTokenSettled(generation, result), () => onTokenSettled(generation, null))
    .catch(() => undefined);
};

const abandonHungTokenRequest = (): void => {
  if (tokenRequestStartedAt !== null && Date.now() - tokenRequestStartedAt >= TOKEN_REQUEST_ABANDON_MS) {
    dropTokenRequest();
    warnTokenFailure();
  }
};

/**
 * Issues the pre-account buffer, then the payload (if any), in creation order; seq is stamped when fetch is
 * called, and no response is ever awaited before the next request is issued. With a fresh cached token the
 * requests are issued synchronously; otherwise they wait for the one shared token request.
 * Resolves once the payload's request has completed or was dropped; never rejects.
 */
const dispatch = (payload: EventPayload | null): Promise<void> => {
  const url = baseUrl;

  if (!url || !isTransportEnabled()) {
    return Promise.resolve();
  }

  abandonHungTokenRequest();

  if (tokenRequestStartedAt !== null) {
    return payload ? waitForToken(payload) : Promise.resolve();
  }

  if (!msalService.getAccount()) {
    if (payload) {
      holdUntilAccount(payload);
    }

    return Promise.resolve();
  }

  const token = freshCachedToken();

  if (token) {
    const batch = payload ? [...takePreAccountBuffer(), payload] : takePreAccountBuffer();

    return Promise.all(batch.map((item) => post(url, token, item))).then(() => undefined, () => undefined);
  }

  if (!payload && !preAccountBuffer.length) {
    return Promise.resolve();
  }

  const sent = payload ? waitForToken(payload) : Promise.resolve();

  startTokenRequest();

  return sent;
};

const onMsalEvent = (message: EventMessage): void => {
  try {
    if (LOGOUT_EVENTS.includes(message.eventType)) {
      dropTokenRequest();

      if (message.eventType === MsalEventType.LOGOUT_START) {
        logoutListener?.();
      }
    } else if (ACCOUNT_EVENTS.includes(message.eventType) && preAccountBuffer.length) {
      dispatch(null);
    }
  } catch (e) {
    // Telemetry must never break MSAL's event dispatch.
  }
};

const registerMsalCallback = (): void => {
  if (msalCallbackRegistered) {
    return;
  }

  try {
    const instance = msalService.getMsalInstance();

    if (instance) {
      msalCallbackRegistered = true;
      instance.addEventCallback(onMsalEvent);
    }
  } catch (e) {
    // Without the callback, a later send that finds an account still flushes the buffer.
  }
};

/** Called on MSAL LOGOUT_START, through the transport's one MSAL event callback. */
export const setLogoutListener = (listener: (() => void) | null): void => {
  logoutListener = listener;
  registerMsalCallback();
};

/**
 * Fire-and-forget (contract §1): never awaited by callers, never retried, never throws.
 * Deliberately not the shared axios client: its interceptors drive the global loading flag and record request
 * errors, and ApiClient.requestHandlerGet/Post/Delete log the user out on 401.
 * Before MSAL has an account the event is held (bounded) and sent, in order, once one is available.
 * While a token request is in flight the event waits for it (bounded); a hung request is abandoned by the next
 * send after TOKEN_REQUEST_ABANDON_MS.
 */
export const sendPayload = (payload: EventPayload): Promise<void> => {
  try {
    if (!baseUrl || !isTransportEnabled()) {
      return Promise.resolve();
    }

    registerMsalCallback();

    return dispatch(payload);
  } catch (e) {
    // Telemetry must never surface an error to the user.
    return Promise.resolve();
  }
};
